import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useConfirmApi } from 'utils/api/ApiHooks';
import { type ThreadedComment } from './threadedComments';

interface CommentsByObjectIdResponse {
  comments_by_object_id: Record<number, ThreadedComment[]>;
}

type ThreadedCommentsByObjectIdContextType = {
  commentsByObjectId: Record<number, ThreadedComment[]> | undefined;
  isLoading: boolean;
  onCommentsChange: (objectId: number, comments: ThreadedComment[]) => void;
};

const ThreadedCommentsByObjectIdContext =
  createContext<ThreadedCommentsByObjectIdContextType | null>(null);

interface ProviderProps {
  // The ContentType id of the commented-on model (e.g. CustomSurveyAnswer).
  contentType: number;
  // The object ids to fetch comment threads for. Empty skips the request.
  objectIds: number[];
}

const ThreadedCommentsByObjectIdProvider: FC<
  PropsWithChildren<ProviderProps>
> = ({ contentType, objectIds, children }) => {
  // clearOnChange resets data/status back to undefined/'LOADING' whenever the
  // params change (deep-compared), so a consumer switching this Provider to a
  // new set of objects never attributes stale comments to the new object ids.
  // `disabled` skips the request entirely when there are no object ids yet -
  // the map stays undefined and consumers fall back to [] per object.
  const { data, status } = useConfirmApi<CommentsByObjectIdResponse>({
    method: 'GET',
    url: 'threaded-comments/bulk',
    params: { content_type: contentType, object_ids: objectIds.join(',') },
    clearOnChange: true,
    disabled: objectIds.length === 0,
  });

  const [commentsByObjectId, setCommentsByObjectId] = useState<
    Record<number, ThreadedComment[]> | undefined
  >(undefined);

  // Adjusted synchronously during render (not via useEffect) so
  // `commentsByObjectId` is never one render stale relative to `status`.
  // A useEffect-based sync would leave a render where `status` has already
  // flipped to 'SUCCESS' (so `isLoading` below reads false) but this state
  // hasn't updated yet - a consumer would mount ThreadedCommentButton with
  // an empty comments array on that render, and its badge count - which
  // only initializes once and is never recomputed from a later `comments`
  // prop - would be stuck at 0 until an actual mutation fixed it.
  const [syncedData, setSyncedData] = useState(data);
  if (data !== syncedData) {
    setSyncedData(data);
    setCommentsByObjectId(data?.comments_by_object_id);
  }

  const onCommentsChange = useCallback(
    (objectId: number, comments: ThreadedComment[]) => {
      setCommentsByObjectId((prev) => ({ ...prev, [objectId]: comments }));
    },
    []
  );

  const value = useMemo(
    () => ({
      commentsByObjectId,
      isLoading: status === 'LOADING',
      onCommentsChange,
    }),
    [commentsByObjectId, status, onCommentsChange]
  );

  return (
    <ThreadedCommentsByObjectIdContext.Provider value={value}>
      {children}
    </ThreadedCommentsByObjectIdContext.Provider>
  );
};

const useThreadedCommentsByObjectId =
  (): ThreadedCommentsByObjectIdContextType => {
    const context = useContext(ThreadedCommentsByObjectIdContext);
    if (!context) {
      throw new Error(
        'useThreadedCommentsByObjectId must be used within a ThreadedCommentsByObjectIdProvider'
      );
    }
    return context;
  };

export { ThreadedCommentsByObjectIdProvider, useThreadedCommentsByObjectId };
